import React, { useEffect, useState } from "react";
import { useRouteMatch } from "react-router";

import withAuthority from "../../../../components/Auth/withAuthority";
import Authorities from "../../../../auth/authorities";
import DefaultAlert from "../../../../components/alerts/DefaultAlert";
import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../utils/consts";
import { Box, Button } from "@material-ui/core";
import _ from "lodash";
import { fetchAllFilterLocations } from "../../../../services/locationApp/locationFilterService";
import InventoryDetailsDefault from "../../../../components/common/InventoryDetails/InventoryDetailsDefault";
import { getFilterListFromArrayObject } from "../../../../utils/commonArrayMap";
import AlertDialog from "../../../../components/alerts/AlertDialog";
import ProductConfigurationNode from "./ProductConfigurationNode";
import {
  createProductConfiguration,
  fetchProductConfigInfo,
  updateProductConfiguration,
} from "../../../../services/menuApp/printerMenuService";
import { AxiosError } from "axios";
import { saveLogs } from "../../../../utils/firebase/logs";
import { getCookie } from "../../../../utils/cookies";
import { useDispatch } from "react-redux";
import { fetchAllMenuDetails, fetchMenus, setProductsVersion } from "../../../../redux/actions/menu";

interface Location {
  id: string;
  businessDisplayName: string;
}

/**
 * ProductConfiguration Component:
 * This React component manages the configuration of stock departments for a given inventory location.
 * It includes functionality to fetch and display master locations, create or update stock configurations,
 * and handle activation requirements. The component is integrated with authentication checks to ensure
 * the user has the necessary authority.
 *
 * Component Structure:
 * - State variables manage component state, including error messages, activation status, and configuration details.
 * - Functions handle API calls, stock configuration updates, and location-specific actions.
 * - useEffect fetches location information and sets the document title on component mount.
 * - JSX structure includes the main container, inventory details component, and alert dialogs.
 *
 * Note: The component is wrapped with the withAuthority HOC to enforce user authority checks.
 */
const ProductConfiguration: React.FunctionComponent = () => {
  const [error, setError] = useState("");
  const [isStockEnable, setIsStockEnable] = useState(true);
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [
    successfullyChangeStockConfiguration,
    setSuccessfullyChangeStockConfiguration,
  ] = useState(false);
  const [locationSelectorList, setLocationSelectorList] = useState([]);
  const [isOpenSkeletonLoading, setIsOpenSkeletonLoading] = useState(true);
  const [success, setSuccess] = useState("");
  const [isEnableMasterLocation, setIsEnableMasterLocation] = useState(false);
  const [selectedDepartmentObj, setSelectedDepartmentObj] = useState<any>({});
  const [selectedDepartmentObjInitial, setSelectedDepartmentObjInitial] =
    useState<any>({});
  const [stockConfiguration, setStockConfiguration] = useState<any>([]);
  const [printerMappingsVersion, setPrinterMappingsVersion] = useState("");
  const [printersVersion, setPrintersVersion] = useState("");
  const [productsVersion, setProductsVersions] = useState("");
  const [printerMappingsVersionInitial, setPrinterMappingsVersionInitial] =
    useState("");
  const [printersVersionInitial, setPrintersVersionInitial] = useState("");
  const [productsVersionInitial, setProductsVersionInitial] = useState("");

  const match: any = useRouteMatch();
  const dispatch = useDispatch();
  /**
   * Function: handleGetMasterLocations
   *
   * Description:
   * This asynchronous function is responsible for fetching master locations for a given inventory location
   * using the 'fetchProductConfigInfo' API. It updates the component state based on the API response,
   * handling cases where the response contains valid data or when there's an error. The function manages
   * the loading state and updates the component state with master location details.
   */
  const handleGetMasterLocations = async (locationDetailsNode: any) => {
    try {
      // Attempt to fetch master locations using the 'fetchProductConfigInfo' API
      const { data } = await fetchProductConfigInfo(match.params.locationId);
      // Check if the response data and 'active' property exist
      if (data && data.data) {
        // Destructure relevant data from the API response
        const {
          masterLocationId,
          productsVersion,
          printersVersion,
          printerMappingsVersion,
        } = data.data;

        // Obtain location details from the filter locations
        const locationDetails = getFilterListFromArrayObject(
          locationDetailsNode,
          masterLocationId,
        );

        // Update component state based on API response
        setStockConfiguration(data.data);
        setSelectedDepartmentObj({
          id: masterLocationId,
          label:
            masterLocationId === match.params.locationId
              ? "Master Location Not Required"
              : locationDetails[0].businessDisplayName,
        });
        setSelectedDepartmentObjInitial({
          id: masterLocationId,
          label:
            masterLocationId === match.params.locationId
              ? "Master Location Not Required"
              : locationDetails[0].businessDisplayName,
        });
        setIsEnableMasterLocation(masterLocationId !== match.params.locationId);
        setPrinterMappingsVersion(printerMappingsVersion);
        setProductsVersions(productsVersion);
        setPrintersVersion(printersVersion);
        setPrinterMappingsVersionInitial(printerMappingsVersion);
        setProductsVersionInitial(productsVersion);
        setPrintersVersionInitial(printersVersion);
      } else {
        setStockConfiguration([]);
        setSelectedDepartmentObj({
          id: match.params.locationId,
          label: "Master Location Not Required",
        });
        setSelectedDepartmentObjInitial({
          id: match.params.locationId,
          label: "Master Location Not Required",
        });
      }

      // Update loading state after API call completion
      setIsOpenSkeletonLoading(false);
    } catch (error) {
      // Handle errors during the API call
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setIsOpenSkeletonLoading(false);
    }
  };

  const handleGetMasterLocations409 = async (locationId: any) => {
    try {
      // Attempt to fetch master locations using the 'fetchProductConfigInfo' API
      const { data } = await fetchProductConfigInfo(match.params.locationId);
      // Check if the response data and 'active' property exist
      if (data && data.data) {
        // Destructure relevant data from the API response
        const { version } = data.data;
        // Update component state based on API response
        setStockConfiguration(data.data);
        handleCreateStockConfiguration(locationId);
      } else {
        // Handle errors during the API call
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
        setIsOpenSkeletonLoading(false);
      }
    } catch (error) {
      // Handle errors during the API call
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setIsOpenSkeletonLoading(false);
    }
  };

  /**
   * Function: handleCreateStockConfiguration
   *
   * Description:
   * This asynchronous function is responsible for creating a new stock configuration for a given location.
   * It prepares the form data, including location and master location details, and calls the 'createMasterLocations'
   * API to create a new stock department. The function handles success and error scenarios, updating the component
   * state accordingly.
   */
  const handleCreateStockConfiguration = async (locationId: any) => {
    // Prepare form data with required information
    const formData = {
      locationId: match.params.locationId, // Assuming match is defined elsewhere
      masterLocationId: locationId,
      productsVersion: productsVersion,
      printerMappingsVersion: printerMappingsVersion,
      printersVersion: printersVersion,
      // active: isActive, // Assuming isActive is defined elsewhere
      version: 0,
    };
    try {
      // Make an asynchronous call to create master locations with the prepared form data
      const res = await createProductConfiguration(
        match.params.locationId,
        formData,
      );

      // Update state and variables upon successful creation
      setSuccessfullyChangeStockConfiguration(true);
      setStockConfiguration(res.data.data);
      setSelectedDepartmentObjInitial({ id: res.data.data.masterLocationId });
      setPrinterMappingsVersionInitial(res.data.data.printerMappingsVersion);
      setProductsVersionInitial(res.data.data.productsVersion);
      setPrintersVersionInitial(res.data.data.printersVersion);
      const version = res.data.data.productsVersion || 1;
      console.log("sdfsfdsgsdgsg", version);
      dispatch(setProductsVersion(version));

            // Always fetch if menus are empty, regardless of isInitialLoad
              console.log("Fetching all menus and details (menus empty)");
              await dispatch(fetchMenus(match.params.locationId));
              await dispatch(fetchAllMenuDetails(match.params.locationId));
          
      
      setIsLoadingButton(false);
    } catch (err) {
      // Handle errors in case of failure
      setSuccessfullyChangeStockConfiguration(false);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setIsLoadingButton(false);
    }
  };

  /**
   * Function: handleUpdateStockConfiguration
   *
   * Description:
   * This asynchronous function is responsible for updating an existing stock configuration for a given location.
   * It prepares the necessary form data, including the ID, location, master location details, and version of the stock configuration,
   * and calls the 'updateMasterLocations' API to update the existing stock department. The function handles both success and error scenarios,
   * updating the component state accordingly.
   */
  const handleUpdateStockConfiguration = async (locationId: any) => {
    // Prepare form data with required information for updating
    const formData = {
      id: stockConfiguration.id, // Assuming stockConfiguration is defined elsewhere
      locationId: match.params.locationId, // Assuming match is defined elsewhere
      masterLocationId: locationId,
      productsVersion: productsVersion,
      printerMappingsVersion: printerMappingsVersion,
      printersVersion: printersVersion,
      // active: isActive, // Assuming isActive is defined elsewhere
      version: stockConfiguration.version, // Assuming stockConfiguration is defined elsewhere
    };

    try {
      // Make an asynchronous call to update master locations with the prepared form data
      const res = await updateProductConfiguration(
        match.params.locationId,
        formData,
      );
      if (res.data.status === 409) {
        handleGetMasterLocations409(locationId);
      } else {
        let summary = [];
        if (formData.productsVersion !== productsVersionInitial) {
          summary.push(
            `Product version changed from ${productsVersionInitial} to ${formData.productsVersion}`,
          );
        }
        if (formData.printerMappingsVersion !== printerMappingsVersionInitial) {
          summary.push(
            `Printer mapping version changed from ${printerMappingsVersionInitial} to ${formData.printerMappingsVersion}`,
          );
        }
        if (formData.printersVersion !== printersVersionInitial) {
          summary.push(
            `Printers version changed from ${printersVersionInitial} to ${formData.printersVersion}`,
          );
        }
        if (selectedDepartmentObjInitial.id !== locationId) {
          summary.push(
            `Master location changed from ${selectedDepartmentObjInitial.id} to ${locationId}`,
          );
        }
        const summaryString = summary.join("\\n");

        // Update state and variables upon successful update
        setSuccessfullyChangeStockConfiguration(true);
        setSelectedDepartmentObjInitial({ id: res.data.data.masterLocationId });
        setPrinterMappingsVersionInitial(res.data.data.printerMappingsVersion);
        setProductsVersionInitial(res.data.data.productsVersion);
        setPrintersVersionInitial(res.data.data.printersVersion);
        setStockConfiguration(res.data.data);
        setIsLoadingButton(false);
        saveLogs(
          {
            message: "Product Configuration Changed",
            summary: summaryString,
            type: "info",
            code: "config-change",
            metadata: { changedBy: "" },
          },
          "menu",
          match.params.locationId,
        );
        const version = res.data.data.productsVersion || 1;
        console.log("sdfsfdsgsdgsg", version);
        dispatch(setProductsVersion(version));
            // Always fetch if menus are empty, regardless of isInitialLoad
              console.log("Fetching all menus and details (menus empty)");
              await dispatch(fetchMenus(match.params.locationId));
              await dispatch(fetchAllMenuDetails(match.params.locationId));
          
      }
    } catch (error) {
      // Handle HTTP 409 conflict by retrying the update
      const err: any = error as AxiosError;
      if (err.response.status === 409) {
        handleGetMasterLocations409(locationId);
      } else {
        // Handle errors in case of failure
        setSuccessfullyChangeStockConfiguration(false);
        setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
        setIsLoadingButton(false);

        saveLogs(
          {
            message: "Error: product configuration changed",
            type: "error",
            code: "config-change",
            metadata: { changedBy: "" },
          },
          "menu",
          match.params.locationId,
        );
      }
    }
  };

  /**
   * Function: handleGetMasterLocationSpecificLocation
   *
   * Description:
   * This asynchronous function is responsible for handling the stock configuration based on the provided location ID.
   * It first checks whether the given location ID matches the current location ID in the route.
   * If true, it checks whether the stock configuration is empty, and if so, it creates a new stock configuration using 'handleCreateStockConfiguration';
   * otherwise, it updates the existing stock configuration using 'handleUpdateStockConfiguration'.
   * If the location IDs don't match, it fetches master locations using the 'fetchProductConfigInfo' API.
   * It then checks if the response data and 'active' property exist. If the master location is active and can be used for stock, it updates the stock configuration;
   * otherwise, it sets appropriate error messages.
   */
  const handleGetMasterLocationSpecificLocation = async (locationId: any) => {
    setIsLoadingButton(true);
    try {
      // Check if the provided location ID matches the current location ID in the route
      if (locationId === match.params.locationId) {
        // If stockConfiguration is empty, create a new stock configuration; otherwise, update the existing one
        _.isEmpty(stockConfiguration)
          ? handleCreateStockConfiguration(locationId)
          : handleUpdateStockConfiguration(locationId);
      } else {
        // Fetch master locations using the 'fetchProductConfigInfo' API
        const { data } = await fetchProductConfigInfo(locationId);

        // Check if the response data and 'active' property exist
        if (data && data.data) {
          // Check if the selected master location can be used for stock
          if (
            data.data.masterLocationId === locationId &&
            data.data.locationId === locationId
          ) {
            setIsStockEnable(true);
            // If stockConfiguration is empty, create a new stock configuration; otherwise, update the existing one
            _.isEmpty(stockConfiguration)
              ? handleCreateStockConfiguration(locationId)
              : handleUpdateStockConfiguration(locationId);
          } else {
            // Set error message if the selected master location cannot be used for stock
            setIsStockEnable(false);
            setErrorMessage("The selected master location cannot be used.");
          }
        } else {
          // Set error message if the selected master location does not have stock enabled
          setIsStockEnable(false);
          setErrorMessage(
            "The selected master location does not have stock enabled.",
          );
        }
        setIsLoadingButton(false);
      }
    } catch (error) {
      setIsLoadingButton(false);
      // Handle errors during the API call
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
  };

  /**
   * Effect to set the document title and fetch location information on component mount.
   */
  useEffect(() => {
    // Set the document title to "Eat Presto - Stock Department"
    document.title = "Menu - Product Configuration";

    // Call the function to fetch master locations when the component mounts
    getAllFilterLocation();
  }, []);

  /**
   * Function to fetch location information and initialize stock department loading.
   * @param {string} searchName - Name to search for locations.
   */
  const getAllFilterLocation = async () => {
    try {
      // Attempt to fetch location information using the 'fetchAllFilterLocations' API
      const res = await fetchAllFilterLocations("");

      // Initialize an array to hold location data
      let locationList: any = [];

      // Check if location data is not empty in the response
      if (!_.isEmpty(res.data.data)) {
        locationList.push({
          id: match.params.locationId,
          label: "Master Location Not Required",
        });
        // Iterate through each location in the response data
        res.data.data.forEach((locationData: Location) => {
          // Push an object with correct property name and label properties to the locationList array
          locationList.push({
            id: locationData.id, // Use the correct property name 'locationId'
            label: locationData.businessDisplayName,
          });
        });
      }
      handleGetMasterLocations(res.data.data);
      // Update the 'locationSelectorList' state with the fetched location list
      setLocationSelectorList(locationList);
    } catch (error) {
      // If an error occurs during the API call, set the 'error' state
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setIsOpenSkeletonLoading(false);
    }
  };

  return (
    <>
      {/* Main container */}
      <Box>
        <InventoryDetailsDefault
          locationSelectorList={locationSelectorList}
          isOpenSkeletonLoading={isOpenSkeletonLoading}
          topic="Product Configuration"
          isNeedAddButton={false}
          nodeList={[1]}
          isActiveLocationId={true}
        >
          <ProductConfigurationNode
            locationSelectorList={locationSelectorList}
            setSelectedDepartmentObj={setSelectedDepartmentObj}
            selectedDepartmentObj={selectedDepartmentObj}
            stockConfiguration={stockConfiguration}
            handleGetMasterLocationSpecificLocation={
              handleGetMasterLocationSpecificLocation
            }
            selectedDepartmentObjInitial={selectedDepartmentObjInitial}
            setIsEnableMasterLocation={setIsEnableMasterLocation}
            isEnableMasterLocation={isEnableMasterLocation}
            printerMappingsVersion={printerMappingsVersion}
            productsVersion={productsVersion}
            printersVersion={printersVersion}
            setPrinterMappingsVersion={setPrinterMappingsVersion}
            setProductsVersion={setProductsVersions}
            setPrintersVersion={setPrintersVersion}
            printerMappingsVersionInitial={printerMappingsVersionInitial}
            productsVersionInitial={productsVersionInitial}
            printersVersionInitial={printersVersionInitial}
            isLoadingButton={isLoadingButton}
          />
        </InventoryDetailsDefault>
        {/* DefaultAlert components for success and error messages */}
        <DefaultAlert
          open={!!success}
          handleClose={() => setSuccess("")}
          message={success}
          severity={"success"}
        />
        <DefaultAlert
          open={!!error}
          handleClose={() => setError("")}
          message={error}
          severity="error"
        />
      </Box>

      {!isStockEnable && (
        <AlertDialog
          open={!isStockEnable}
          title="Activation Required"
          desc={errorMessage}
          severity="error"
          confirmLabel="Ok"
          disableBackdropClick
          confirmAction={() => {
            setIsStockEnable(true);
          }}
          color="secondary"
        />
      )}

      {successfullyChangeStockConfiguration && (
        <AlertDialog
          open={successfullyChangeStockConfiguration}
          title="Success"
          desc="Successfully change product configuration."
          severity="success"
          confirmLabel="Ok"
          disableBackdropClick
          confirmAction={() => {
            setSuccessfullyChangeStockConfiguration(false);
          }}
          color="secondary"
        />
      )}
    </>
  );
};

export default withAuthority(
  ProductConfiguration,
  Authorities.STOCK_CONFIGURATION_READ,
);
